<template>
	<div v-if="viewLoaded">
		<h1 class="mb-3 ml-3 contest-panel-view-title">{{projectData.title || ""}}</h1>
		<v-stepper v-model="viewData.step" :vertical="true">
			<v-stepper-header style="height: auto">
				<template v-for="(section, index) in totalNumberOfSteps">
					<v-stepper-step edit-icon="check" :complete="viewData.step > index+1" :step="index+1" :key="index"
						:editable="true">
					</v-stepper-step>
				</template>
			</v-stepper-header>

			<div v-if="blockActions">
				<v-layout row align-center justify-center>
					<v-flex xs12>
						<v-alert class="ma-4" :value="blockActions" type="warning">
							Tryb podglądu.
						</v-alert>
					</v-flex>
				</v-layout>
				<v-layout row align-center justify-center>
					<v-flex xs8/>
					<v-flex xs4>
						<div style="display: flex; justify-content: center">
							<v-btn @click="editMode" color="primary">Powrót do trybu edycji</v-btn>
						</div>
					</v-flex>
				</v-layout>
			</div>

			<v-stepper-items>
				<v-btn @click="nextStep()" v-if="!isLastStep && !blockActions" :disabled="savingProject" color="primary" class="right">
					<v-icon class="mr-2 white--text">arrow_forward</v-icon>
					Dalej
				</v-btn>

				<v-btn @click="stepBack()" v-if="!isFirstStep && !blockActions" :disabled="savingProject" color="default" class="right">
					<v-icon class="mr-2 white--text">arrow_back</v-icon>
					Cofnij
				</v-btn>
				<h1 class="stepTitle">{{stepTitle}}</h1>
				<project-name
					v-if="stepTitle === 'Podstawowe informacje'"
					v-model="projectData.title"
				></project-name>

				<keep-alive>
					<project-english-name
						v-if="stepTitle === 'Podstawowe informacje'"
						v-model="projectData.englishTitle"
						ref="englishName"
						:readonly="blockActions"
					></project-english-name>
				</keep-alive>

				<keep-alive>
					<priority-tree
						v-if="stepTitle === 'Podstawowe informacje' && projectData.contestId"
						:contest-id="projectData.contestId"
						v-model="projectData.prioritiesData"
						:many-accreditation-countries="contestConfiguration.manyAccreditationCountries"
						:many-areas="contestConfiguration.manyAreas"
						:many-priorities="contestConfiguration.manyPriorities"
						:many-tasks="contestConfiguration.manyTasks"
						@countriesChanged="changeCountries"
						ref="priorityTree"
						:readonly="blockActions"
					></priority-tree>
				</keep-alive>

				<project-other-bidders
					v-if="stepTitle === 'Podstawowe informacje'"
					v-model="projectData.otherBiddersId"
					:mutual-project="projectData.mutualProject"
					@change="(val) => mutualProjectChanged(val)"
					@biddersChanged="setBiddersChanged"
					ref="otherBidders"
					:readonly="blockActions"
				></project-other-bidders>

				<choose-funding
					ref="funding"
					:contest-id="projectData.contestId"
					:input="projectData.prioritiesData"
					v-if="stepTitle === 'Statystyka'"
					v-model="projectData.financesData"
					:countries-changed.sync="countriesChanged"
					:readonly="blockActions"
				></choose-funding>
				<br>

				<choose-d-a-c
					v-if="stepTitle === 'Statystyka' && contestConfiguration.dacCodesEnabled"
					:contest-id="projectData.contestId"
					type="PUBLIC_CONTEST"
					ref="chooseDac"
					v-model="projectData.dacCodes"
					:readonly="blockActions"
				></choose-d-a-c>

				<choose-start-end
					v-if="stepTitle === 'Ramy czasowe'"
					v-model="projectData.dates"
					ref="startEnd"
					:readonly="blockActions"
				></choose-start-end>

				<choose-volunteers
					v-if="stepTitle === 'Ramy czasowe' && contestConfiguration.volunteersAvailable"
					v-model="projectData.volunteers"
					:global-init="projectData.globalInitiative"
					:global-init-description="projectData.globalInitiativeDescription"
					@change="(val) => globalInitiativeChanged(val)"
					:module-start-date="projectData.dates.start"
					:module-end-date="projectData.dates.end"
					:volunteers-required="contestConfiguration.volunteersRequired"
					:volunteer-limit="contestConfiguration.volunteerLimit"
					:min-days-abroad="contestConfiguration.minDaysAbroad"
					:number-of-modules="0"
					ref="volunteers"
					:readonly="blockActions"
				></choose-volunteers>

				<project-choose-actions
					v-if="stepTitle === 'Działania programowe'"
					:actions.sync="projectData.actionsData"
					:actions-costs.sync="projectData.actionsCosts"
					:actions-costs-action-ids.sync="projectData.actionsCostsActionIds"
					:contest-id="projectData.contestId"
					:taken-serial-numbers="projectData.serialNumbers"
					@update-actions-and-costs="updateActionsAndCosts"
					:module-id="1"
					:readonly="blockActions"
					ref="actions"
				></project-choose-actions>

				<choose-geographical-locations
					v-if="stepTitle === 'Działania programowe'"
					v-model="projectData.locations"
					ref="locations"
					:readonly="blockActions"
				></choose-geographical-locations>

				<contest-offer-timetable
					v-if="stepTitle === 'Harmonogram'"
					:actions="projectData.actionsData"
				></contest-offer-timetable>

				<project-costs-table
					v-if="stepTitle === 'Koszty'"
					:actions="projectData.actionsData"
					:actions-costs.sync="projectData.actionsCosts"
					:administrative-costs.sync="projectData.administrativeCosts"
					:actions-costs-actions-ids.sync="projectData.actionsCostsActionIds"
					:offer-configuration="contestConfiguration"
					:taken-serial-numbers="projectData.serialNumbers"
					ref="costs"
					:readonly="blockActions"
				></project-costs-table>

				<financial-table-hint
					v-if="stepTitle === 'Koszty'"
					:hint="contestConfiguration.financialTableHint"
				></financial-table-hint>

				<ProjectCostsSummaryTable
					v-if="stepTitle === 'Podsumowanie kosztów' && !savingProject"
					:costs="projectData.actionsCosts"
					:administrative="projectData.administrativeCosts"
					:offer-configuration="contestConfiguration"
					:main-country-id="projectData.prioritiesData.mainCountryId"
					:main-bidder-id="projectData.mainBidderId"
					:contest-id="projectData.contestId"
					ref="costsSummary"
				></ProjectCostsSummaryTable>

				<ProjectBidderFundingsTable
					v-if="stepTitle === 'Podział kosztów' && projectData.mutualProject"
					:costs="projectData.actionsCosts"
					:administrative="projectData.administrativeCosts"
					:fundings="projectData.bidderFundings"
					:mutual="projectData.mutualProject"
					:bidders-changed="biddersChanged && !biddersReloaded"
					@reloadFundings="reloadBidderFundings"
					ref="bidderFundings"
					:readonly="blockActions"
				></ProjectBidderFundingsTable>

				<vue-form-generator
					name="generator"
					ref="vfg"
					:model="formGenerator.currentModel"
					:options="formGenerator.formOptions"
					:schema="formGenerator.currentSchema"
					:disabled="blockActions"
					v-if="viewData.showGenerator"
				/>

				<v-layout mt-3 v-if="!blockActions">
					<v-btn @click="$router.go(-1)" v-if="isFirstStep" color="blue">
						<v-icon class="mr-2">close</v-icon>
						Anuluj
					</v-btn>

					<v-btn @click="stepBack()" v-if="!isFirstStep" :disabled="savingProject" color="default">
						<v-icon class="mr-2 white--text">arrow_back</v-icon>
						Cofnij
					</v-btn>

					<v-btn @click="nextStep()" v-if="!isLastStep" :disabled="savingProject" color="primary">
						<v-icon class="mr-2 white--text">arrow_forward</v-icon>
						Dalej
					</v-btn>

					<v-btn @click="saveProject(true, viewData.step)" :disabled="savingProject" color="info">
						<v-icon class="mr-2">save</v-icon>
						Zapisz projekt
					</v-btn>

					<v-btn @click="completeProjectDialog = true" :disabled="savingProject" color="primary"
						v-if="isLastStep && roles.isSubBidder()">
						<v-icon class="mr-2 white--text">check</v-icon>
						Zakończ wypełnianie projektu
					</v-btn>

					<v-btn v-if="isLastStep && roles.isBidder()" @click="sendProjectDialog = true" :disabled="savingProject" color="success">
						<v-icon class="mr-2">send</v-icon>
						Zakończ i wyślij projekt
					</v-btn>
				</v-layout>

				<v-alert :value="roles.isSubBidder()" type="warning" v-if="isLastStep">
					Projekt wymaga potwierdzenia przez głównego oferenta. Oferent główny otrzyma prośbę
					o potwierdzenie projektu dopiero gdy zakończysz jego wypełnianie.
				</v-alert>

			</v-stepper-items>
		</v-stepper>

		<project-info-dialog :action="closePeekModeWarning" :dialog-model="peekModeWarning"
			header="Przejście do trybu podglądu" content="Nastąpiło przejście do trybu podglądu. Aby edytować/zapisywać projekt, należy wrócić do trybu edycji."/>


		<ProjectConfirmationDialog :action="send" :dialog-model="sendProjectDialog"
			@close-confirmation="sendProjectDialog = false" action-label="Wyślij"
			content="Upewnij się, że wypełniłeś poprawnie formularz zanim zatwierdzisz projekt.
				Po zatwierdzeniu nie będzie możliwości edycji!" header="Wysyłka projektu" icon="send"/>

		<ProjectConfirmationDialog :action="send" :dialog-model="completeProjectDialog"
			@close-confirmation="completeProjectDialog = false" action-label="Zakończ i wyślij"
			content="Upewnij się, że poprawnie wypełniłeś formularz.
				Po wysłaniu projektu nie będzie możliwości powrotu do jego edycji!" header="Koniec wypełniania projektu"
			icon="check">
			<div>
				<v-tooltip top v-if="subBidderType === 'SUBSIDIARY' && roles.isSubBidder()">
					<template v-slot:activator="{ on }">
						<div v-on="on">
							<v-checkbox
								class="ml-3"
								label="Pozwól na edycję przez głównego oferenta"
								v-model="projectData.allowMainBidderChanges"
								aria-label="allow main bidder changes"/>
						</div>
					</template>
					<span>
						Pozwól głównemu oferentowi na dokonywanie zmian w wysłanym projekcie.<br/>
						W przeciwnym wypadku główny oferent nie może edytować wysłanego projektu.
					</span>
				</v-tooltip>
			</div>
		</ProjectConfirmationDialog>
	</div>
</template>

<script>
	import Vue from "vue";
	import {Project} from "@/api/generated/Project";
	import ProjectName from "./components/ProjectName";
	import ProjectEnglishName from "./components/ProjectEnglishName";
	import ChooseDAC from "../offer/components/ChooseDAC";
	import ChooseFunding from "../offer/components/ChooseFunding";
	import PriorityTree from "../offer/components/PriorityTree";
	import ChooseStartEnd from "../offer/components/ChooseStartEnd";
	import ChooseGeographicalLocations from "../offer/components/ChooseGeographicalLocations";
	import ContestOfferTimetable from "../offer/components/ContestOfferTimetable";
	import VueFormGenerator from "vue-form-generator";
  import ProjectBidderFundingsTable from "./components/ProjectBidderFundingsTable";
	import ProjectCostsSummaryTable from "./components/ProjectCostsSummaryTable";
	import {Api} from "@/api";
	import {genericFormUtil} from "@/utils/genericFormUtil";
	import ChooseVolunteers from "../offer/components/ChooseVolunteers";
	import ProjectConfirmationDialog from "@/view/logged/project/components/ProjectConfirmationDialog";
	import {SubBidder} from "@/api/generated/SubBidder";
  import ProjectInfoDialog from "@/view/logged/project/components/ProjectInfoDialog";
  import FinancialTableHint from "@/view/logged/offer/components/FinancialTableHint";
  import ProjectOtherBidders from "@/view/logged/project/components/ProjectOtherBidders";
  import ProjectChooseActions from "@/view/logged/project/components/ProjectChooseActions";
  import ProjectCostsTable from "@/view/logged/project/components/CostsTableComponents/ProjectCostsTable";

	Vue.use(VueFormGenerator);

	export default {
		name: "ProjectView",

		components: {
      ProjectCostsTable,
      ProjectChooseActions,
      ProjectOtherBidders,
      FinancialTableHint,
      ProjectInfoDialog,
			ProjectConfirmationDialog,
			ChooseVolunteers,
      ProjectBidderFundingsTable,
      ProjectCostsSummaryTable,
			ProjectName,
			ProjectEnglishName,
			ChooseDAC,
			ChooseFunding,
			PriorityTree,
			ChooseStartEnd,
			ChooseGeographicalLocations,
			ContestOfferTimetable,
		},

		computed: {
			viewLoaded() {
				return this.projectDataLoaded && this.contestConfigurationLoaded;
			},

			stepTitle() {
				return this.viewData.titles.allTitles[this.viewData.step - 1];
			},

			totalNumberOfSteps() {
				return this.viewData.titles.allTitles.length;
			},

			isFirstStep() {
				return this.viewData.step === 1;
			},

			isLastStep() {
				return this.viewData.step === this.totalNumberOfSteps;
			},

			numberOfFirstNotModuleSteps() {
				return this.viewData.titles.firstNotModuleStepTitles.length;
			},

			numberOfConstFieldsModuleSteps() {
				return 0;
			},

			numberOfGenericPersistentSteps() {
				return this.contestConfiguration.persistentSchema.sections.length;
			},

			numberOfLastNotGenericSteps() {
				return this.viewData.titles.lastNotGenericStepTitles.length;
			},
		},

		watch: {
			"formGenerator.currentModel": {
				handler() {
					if (this.viewLoaded && this.isGenericPersistentStep) {
						this.applyGenericPersistentForm();
					}
				},
				deep: true,
			},

			"viewData.step": { // krok, może zostać zmieniony poprzez kliknięcie w stepper-header-step lub poprzez nextStep
				handler(newValue, oldValue) {
          this.handleStepChange(newValue, oldValue);
				},
			},

      sendOrSubmitAvailable(value) {
        if (value) {
          if (this.acceptanceProjectRequest) {
            this.sendAcceptanceProjectRequest();
            this.acceptanceProjectRequest = false;
          } else if (this.submitProjectRequest) {
            this.sendSubmitProjectRequest();
            this.submitProjectRequest = false;
          }
          this.sendOrSubmitAvailable = false;
        }
      },
		},

		data() {
			return {
        biddersChanged: false,
        biddersReloaded: false,
				savingProject: false,
				contestConfiguration: {
					persistentSchema: null,
					firstPossibleOfferDate: "",
					lastPossibleOfferDate: "",
				},
				subBidderType: "",
				countriesChanged: false,
				projectId: this.$route.params.projectId,
				projectData: {
					prioritiesData: {
						selected: [],
					},
					allowMainBidderChanges: false,
				},
				viewData: {
					step: null,
					titles: {
						firstNotModuleStepTitles: ["Podstawowe informacje", "Statystyka", "Ramy czasowe", "Działania programowe", "Koszty", "Podsumowanie kosztów", "Podział kosztów", "Harmonogram"],
						lastNotGenericStepTitles: ["Składanie projektu"],
						allTitles: [],
					},
					showGenerator: false,
				},
				formGenerator: {
					currentSchema: {},
					currentModel: {},
					formOptions: {
						validateAfterLoad: false,
						validateAfterChanged: true,
						validateAsync: true,
					},
					currentConditionalBooleans: [],
				},
				sendProjectDialog: false,
				completeProjectDialog: false,
        peekModeWarning: false,
				contestConfigurationLoaded: false,
				projectDataLoaded: false,
        blockActions: false,
        stepChangeWithValidation: false,
        stepInit: true,

        saveRequestError: false,
        acceptanceProjectRequest: false,
        submitProjectRequest: false,
        sendOrSubmitAvailable: false,
			};
		},

		created() {
			this.loadData();
		},

		methods: {
      editMode() {
        this.blockActions = false;
        this.loadData();
      },

      closePeekModeWarning() {
        this.peekModeWarning = false;
      },

      async handleStepChange(newValue, oldValue) {
        if (this.stepInit) {
          this.stepInit = !this.stepInit;
          this.changingStepAction();
          this.$vuetify.goTo(0, {easing: 'linear'});
          return;
        }
        if (!this.blockActions && !this.stepChangeWithValidation && newValue > oldValue) {
          await this.saveProject(false, oldValue);
          this.blockActions = true;
          this.peekModeWarning = true;
        }
        this.stepChangeWithValidation = false;
        this.changingStepAction();
        this.$vuetify.goTo(0, {easing: 'linear'});
      },

			validateExistingRef(name) {
				if (this.$refs[name] === undefined) {
					return true;
				}
				if (typeof this.$refs[name]["validate"] !== "function") {
					return true;
				}
				let res = this.$refs[name].validate();
				return res;
			},

			initializeStepTitles() {
        this.viewData.titles.allTitles = [];
				this.viewData.titles.firstNotModuleStepTitles.forEach(title => {
              if (title !== "Podział kosztów" || this.projectData.mutualProject) {
                this.viewData.titles.allTitles.push(title);
              }
				});
				this.viewData.titles.allTitles.push();

				for (let i = 0; i < this.numberOfGenericPersistentSteps; i++) {
					this.viewData.titles.allTitles.push(this.contestConfiguration.persistentSchema.sections[i].name);
				}
				this.viewData.titles.lastNotGenericStepTitles.forEach(title => {
					this.viewData.titles.allTitles.push(title);
				});
			},

			validateNonGeneric() {
				let res = this.validateExistingRef("englishName") &&
					this.validateExistingRef("priorityTree") &&
					this.validateExistingRef("chooseDac") &&
					this.validateExistingRef("actions") &&
					this.validateExistingRef("startEnd") &&
					this.validateExistingRef("volunteers") &&
					this.validateExistingRef("costs") &&
					this.validateExistingRef("costsSummary") &&
					this.validateExistingRef("bidderFundings") &&
					this.validateExistingRef("locations");
				return res;
			},

			async validate() {
				let isValid = true;

				if (this.isGenericPersistentStep()) {
					let validationPromise = await this.$refs.vfg.validate();
					if (validationPromise.length > 0) {
						isValid = false;
					}
					return isValid;
				}
				return isValid;
			},

			async loadData() {
				await Api.syncGet(this, "/project/configuration?projectId=".concat(this.projectId), response => {
						this.contestConfiguration.persistentSchema = genericFormUtil.decodeSchema(response.genericPartScheme);
						this.contestConfiguration.financialTableHint = response.financialTableHint;
						this.contestConfiguration.volunteersAvailable = response.volunteersAvailable;
						this.contestConfiguration.volunteersRequired = response.volunteersRequired;
						this.contestConfiguration.volunteerLimit = response.volunteerLimit;
						this.contestConfiguration.minDaysAbroad = response.minDaysAbroad;
						this.contestConfiguration.manyAccreditationCountries = response.manyAccreditationCountries;
						this.contestConfiguration.manyAreas = response.manyAreas;
						this.contestConfiguration.manyPriorities = response.manyPriorities;
						this.contestConfiguration.manyTasks = response.manyTasks;
						this.contestConfiguration.percentAdministrativeLimit = response.percentAdministrativeLimit;
						this.contestConfiguration.percentAdministrativeRelativeToWholeCosts = response.percentAdministrativeRelativeToWholeCosts;
						this.contestConfiguration.percentMinOwnContributionLimit = response.percentMinOwnContributionLimit;
						this.contestConfiguration.percentMinOwnContributionRelativeToWholeCosts = response.percentMinOwnContributionRelativeToWholeCosts;
						this.contestConfiguration.percentMaxOwnContributionLimit = response.percentMaxOwnContributionLimit;
						this.contestConfiguration.percentMaxOwnContributionRelativeToWholeCosts = response.percentMaxOwnContributionRelativeToWholeCosts;
						this.contestConfiguration.offerModuleNumber = response.offerModuleNumber;
						this.contestConfiguration.dacCodesEnabled = response.dacCodesEnabled;
						this.contestConfigurationLoaded = true;
					},
					errorResponse => this.$messagesBus.$emit("error", "Nie udało się wczytać konfiguracji projektu!", errorResponse),
				);

				Project.get(this, this.projectId, response => {
          this.stepInit = true;
					this.projectData = response;
					this.projectData.genericModel = genericFormUtil.decodeSchema(response.genericModel);
          if (this.viewData.step === response.step) {
            this.handleStepChange(response.step, response.step);
          } else {
            this.viewData.step = response.step;
          }
					this.projectDataLoaded = true;
					this.initializeStepTitles();
					if (this.isGenericPersistentStep()) {
						this.applyGenericPersistentForm();
					}
				});

				if (this.roles.isSubBidder()) {
					SubBidder.getSubBidderType(this,
						success => {
							this.subBidderType = success.subBidderType;
						});
				}
			},

			resolveSectionIndexInPersistentSchema() {
				return this.numberOfGenericPersistentSteps - (this.totalNumberOfSteps - this.numberOfLastNotGenericSteps - this.viewData.step) - 1;
			},

			applyGenericPersistentForm() {
				if (!this.projectData.genericModel) return;
				let sections = this.projectData.genericModel.sections;
				let currentSectionIndex = this.resolveSectionIndexInPersistentSchema();
				this.formGenerator.currentModel = sections[currentSectionIndex].fields;
				let schema = genericFormUtil.getSectionSchemaFromSchema(this.contestConfiguration.persistentSchema, currentSectionIndex);
				if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
					let schemaValues = sections[currentSectionIndex].fields;
					this.formGenerator.currentSchema = genericFormUtil.filterSchemaByConditionalFieldsValue(schema, schemaValues);
					let indexes = genericFormUtil.calculateIndexes(schema);
					this.formGenerator.currentConditionalHashes = genericFormUtil.getConditionalValueHashes(schema, indexes);
				} else {
					this.formGenerator.currentSchema = schema;
				}
				this.viewData.showGenerator = true;
			},

      mutualProjectChanged(val) {
        this.projectData.mutualProject = val;
        this.projectData.otherBiddersId = [];
        this.initializeStepTitles();
      },

      setBiddersChanged() {
        this.biddersChanged = true;
        this.biddersReloaded = false;
      },

      reloadBidderFundings() {
        Project.getBidderFundings(this, this.$route.params.projectId, response => {
          this.projectData.bidderFundings = response;
          this.biddersReloaded = true;
          this.biddersChanged = false;
        });
      },

			globalInitiativeChanged (val) {
				this.projectData.globalInitiative = val.isGlobal;
				this.projectData.globalInitiativeDescription = val.isGlobal ? val.description : null;
			},

			isGenericPersistentStep() {
				let step = this.viewData.step;
				return (step > this.totalNumberOfSteps - this.numberOfLastNotGenericSteps - this.numberOfGenericPersistentSteps) && (step <= this.totalNumberOfSteps - this.numberOfLastNotGenericSteps);
			},

			async nextStep() {
				let valid = await this.validate();
				valid &= this.validateNonGeneric();
				if (!valid) {
					this.$messagesBus.$emit("error", "Formularz zawiera błędy");
					return;
				}
        this.saveInViewCurrentStepState();
        this.stepChangeWithValidation = true;
				this.viewData.step = this.viewData.step + 1;
				this.projectData.step = this.viewData.step;
				this.changingStepAction();
				await this.saveProject(true, this.viewData.step);
			},

			saveInViewCurrentStepState() {
				if (this.isGenericPersistentStep() && this.projectData.genericModel) {
					this.projectData.genericModel.sections[this.resolveSectionIndexInPersistentSchema()].fields = this.formGenerator.currentModel;
				}
			},

			stepBack() {
				this.viewData.step = this.viewData.step - 1;
				this.projectData.step = this.viewData.step;
				this.changingStepAction();
			},

			changeCountries() {
				this.countriesChanged = true;
			},

			changingStepAction() {
				if (this.isGenericPersistentStep()) {
					this.applyGenericPersistentForm();
					this.viewData.showGenerator = true;
				} else {
					this.viewData.showGenerator = false;
				}
			},

      updateActionsAndCosts(value, index1, index2) {
        this.projectData.actionsData = value;
        let costsTmp = this.projectData.actionsCosts[index1];
        this.projectData.actionsCosts[index1] = this.projectData.actionsCosts[index2] || [];
        this.projectData.actionsCosts[index2] = costsTmp || [];
        let idsTmp = this.projectData.actionsCostsActionIds[index1];
        this.projectData.actionsCostsActionIds[index1] = this.projectData.actionsCostsActionIds[index2];
        this.projectData.actionsCostsActionIds[index2] = idsTmp;
      },

      lockProject() {
        this.savingProject = true;
      },

      unlockProject() {
        this.savingProject = false;
      },

			async saveProject (showMessage, step) {
        this.lockProject();
				this.projectData.step = step;
        this.saveProjectData(showMessage);
			},

      saveProjectData(showMessage) {
        let projectData = this.prepareProjectData();
        Project.putData(this, this.projectId, projectData,
            () => {
              this.saveProjectForm(showMessage);
            },
            () => {
              this.saveRequestError = true;
              this.saveProjectForm(showMessage);
            });
      },

      saveProjectForm(showMessage) {
        let projectForm = this.prepareProjectForm();
        Project.putForm(this, this.projectId, projectForm,
            () => {
              this.checkErrorsAndUnlockProject(showMessage);
            },
            () => {
              this.saveRequestError = true;
              this.checkErrorsAndUnlockProject(showMessage);
            });
      },

      prepareProjectData() {
        let projectDataCopy = JSON.parse(JSON.stringify(this.projectData));
        // model is being saved in the next request
        delete projectDataCopy.genericModel;
        // to avoid WAF false-positives (bidder web pages etc.), bidder data is immutable anyway - no reason to send
        delete projectDataCopy.mainBidder;
        delete projectDataCopy.otherBidders;
        return projectDataCopy;
      },

      prepareProjectForm() {
        return {
          genericModel: genericFormUtil.encodeSchema(JSON.parse(JSON.stringify(this.projectData.genericModel)))
        };
      },

      sendAcceptanceProjectRequest() {
        let successHandler = successResponse => {
          this.$router.go(-1);
          this.$messagesBus.$emit("success", "Projekt został pomyślnie wysłany", successResponse);
          this.unlockProject();
        };
        let errorHandler = errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się wysłać projektu", errorResponse);
          this.unlockProject();
        };
        Project.putSendForAcceptance(this, this.projectId, successHandler, errorHandler);
      },

      sendSubmitProjectRequest() {
        let successHandler = successResponse => {
          this.$router.go(-1);
          this.$messagesBus.$emit("success", "Projekt został pomyślnie wysłany", successResponse);
          this.unlockProject();
        };
        let errorHandler = errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się wysłać projektu", errorResponse);
          this.unlockProject();
        };
        Project.putSubmit(this, this.projectId, successHandler, errorHandler);
      },

      checkErrorsAndUnlockProject(showMessage) {
        if (this.saveRequestError) {
          this.$messagesBus.$emit("error", "Nie udało się zapisać projektu");
          this.saveRequestError = false;
          this.acceptanceProjectRequest = this.submitProjectRequest = false;
        } else if (showMessage) {
          this.$messagesBus.$emit("success", "Projekt został pomyślnie zapisany");
        }
        this.sendOrSubmitAvailable = true;
        this.unlockProject();
      },

			send() {
        if (this.roles.isBidder()) {
          this.submitProjectRequest = true;
        }
        if (this.roles.isSubBidder()) {
          this.acceptanceProjectRequest = true;
        }
        this.saveProject(false, this.viewData.step);
			}
		},
	};
</script>
